import React, { useEffect, useRef } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import Modal from './Modal';

/**
 * The dialog half of useConfirm. Rendered once at the app root by
 * ConfirmProvider — call sites never mount this directly.
 *
 * Focus lands on the safe choice rather than the destructive one, because
 * this replaces window.confirm, where the same Enter keystroke cancelled.
 * Keeping that muscle memory matters more here than saving a Tab press:
 * these dialogs mostly guard deletes.
 */
const ConfirmDialog = ({
    open,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    busy = false,
    onConfirm,
    onCancel
}) => {
    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        // Defer past the CSSTransition mount, otherwise the node is not yet
        // focusable and the keyboard is left on the page behind the scrim.
        const id = window.setTimeout(() => {
            const target = destructive ? cancelRef.current : confirmRef.current;
            target?.focus();
        }, 50);
        return () => window.clearTimeout(id);
    }, [open, destructive]);

    // A minimal focus trap: without it Tab walks into the page behind the
    // scrim, which is reachable but invisible.
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (event) => {
            if (event.key !== 'Tab') return;
            const nodes = [cancelRef.current, confirmRef.current].filter(Boolean);
            if (nodes.length === 0) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    const Icon = destructive ? AlertTriangle : HelpCircle;

    return (
        <Modal isOpen={open} onClose={busy ? () => {} : onCancel} size="sm">
            <div className="p-6" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
                <div className="flex gap-4">
                    <div
                        className={`shrink-0 flex items-center justify-center w-10 h-10 rounded-full ${
                            destructive ? 'bg-red-100' : 'bg-indigo-100'
                        }`}
                    >
                        <Icon
                            className={`w-5 h-5 ${destructive ? 'text-red-600' : 'text-indigo-600'}`}
                            aria-hidden="true"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 id="confirm-title" className="text-base font-medium text-gray-900">
                            {title}
                        </h3>
                        {message && (
                            <p className="mt-1 text-sm text-gray-500 whitespace-pre-line">{message}</p>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className={`inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                            destructive
                                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                        }`}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ConfirmDialog;
